import {
  loadAndInstallCoreBalance,
  type WorldHostPresentationPrefs,
  type WorldSavedGameBundle,
} from "@dawn/adapters";
import { createGameSession } from "@/host/createGameSession";
import type { WorldMapScenario, WorldState } from "@dawn/engine";
import {
  clearStaleDeployReloadAttempt,
  tryRecoverFromStaleDeploy,
} from "@site/launch/staleDeployRecovery";

export interface GameLifecycleElements {
  canvas: HTMLCanvasElement;
  hudRoot: HTMLElement;
}

export interface GameLaunchOptions {
  worldSavedSnapshot?: WorldState | null;
  worldSavedPresentation?: WorldHostPresentationPrefs | null;
  worldSavedMapScenario?: WorldMapScenario | null;
}

interface GameLifecycleHandlers {
  onBecameActive: () => void;
  onExit: () => void;
  onFailed: () => void;
}

export interface GameLifecycle {
  launch(
    options: GameLaunchOptions,
    handlers: GameLifecycleHandlers,
  ): Promise<void>;
  dispose(): void;
}

export type { WorldSavedGameBundle };

export function createGameLifecycle(
  elements: GameLifecycleElements,
): GameLifecycle {
  const { canvas, hudRoot } = elements;
  let activeHost: { dispose(): void } | null = null;

  const showGameChrome = (): void => {
    canvas.hidden = false;
    hudRoot.hidden = false;
    document.body.classList.add("game-active");
  };

  const hideGameChrome = (): void => {
    canvas.hidden = true;
    hudRoot.hidden = true;
    document.body.classList.remove("game-active");
  };

  return {
    async launch(options, handlers): Promise<void> {
      try {
        activeHost?.dispose();

        await loadAndInstallCoreBalance();

        handlers.onBecameActive();
        showGameChrome();

        const host = await createGameSession({
          canvas,
          hudRoot,
          onExit: () => {
            activeHost?.dispose();
            activeHost = null;
            hideGameChrome();
            handlers.onExit();
          },
        });

        if (options.worldSavedSnapshot) {
          await host.launch({
            worldSavedSnapshot: options.worldSavedSnapshot,
            worldSavedPresentation: options.worldSavedPresentation,
            worldSavedMapScenario: options.worldSavedMapScenario,
          });
        } else {
          await host.launch({});
        }

        activeHost = host;
        clearStaleDeployReloadAttempt();
      } catch (error) {
        if (tryRecoverFromStaleDeploy(error)) {
          return;
        }

        handlers.onFailed();
        console.error("[session] game_launch_failed", error);
        throw error;
      }
    },
    dispose(): void {
      activeHost?.dispose();
      activeHost = null;
      hideGameChrome();
    },
  };
}
